import { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { useInfiniteScrollQuery } from '@/lib/infinite-query-utils.js';
import { EmptyArray, getReviewHistory } from '@/lib/api-calls.js';
import { formatInstant, FrequentTimeZones } from '@airreading/common';
import {
  GetStudentReviewHistoryResults,
  SessionFeedbackIssueDetails,
  type StudentReviewHistory,
} from '@airreading/common/data';

import { InfiniteScrollTable } from '@/components/table/infinite-scroll-table.js';
import { StudentFeedbackIssueBadge } from '@/components/feedbacks/student-feedback-issue-badge.js';
import { PeopleBadgeList } from '../../scheduling/student-groups/-components/people-badge-list.js';
import { ReviewHistoryMetadata } from './review-history-metadata.js';

const col = createColumnHelper<StudentReviewHistory>();
const columns = [
  col.accessor((row) => row, {
    id: 'reviewHistory',
    header: 'Review History',
    cell: ({ getValue }) => {
      const reviewHistory = getValue();
      return (
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <span className="font-bold">
                {formatInstant(reviewHistory.dateInstant, FrequentTimeZones.UsCentral).formattedDate}
              </span>
              <span>{reviewHistory.lesson?.name}</span>
              {reviewHistory.issue && <StudentFeedbackIssueBadge studentFeedbackIssue={reviewHistory.issue} />}
            </div>
            <PeopleBadgeList people={[reviewHistory.coach]} />
          </div>
          <ReviewHistoryMetadata
            studentAttended={reviewHistory.studentAttended}
            studentIssueLabels={reviewHistory.studentIssues.map((issue) => SessionFeedbackIssueDetails[issue].label)}
            classNotes={reviewHistory.classNotes}
            description={reviewHistory.description}
          />
        </div>
      );
    },
  }),
];

export function StudentReviewHistory({ studentId }: { studentId: string }) {
  const requestQuery = useMemo(
    () => (pageIndex: number, pageSize: number) => getReviewHistory(studentId, { pageIndex, pageSize }),
    [studentId],
  );

  const {
    data: response,
    isPending: isLoadingReviewHistory,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteScrollQuery<GetStudentReviewHistoryResults>(requestQuery, (result) => result.reviewHistory);

  const reviewHistory = useMemo(() => response?.pages.flatMap((page) => page.reviewHistory) || EmptyArray, [response]);
  const totalRowCount = useMemo(() => response?.pages[0]?.total || 0, [response]);

  const table = useReactTable({
    data: reviewHistory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
    rowCount: totalRowCount,
  });

  return (
    <InfiniteScrollTable
      emptyState="No review history found"
      table={table}
      isLoading={isLoadingReviewHistory}
      hasError={!!error}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      showTableHeader={false}
      // mt-[-1.5rem] is to offset the top border of the table due to border-spacing-y-6
      rowClassName="whitespace-pre-line break-words"
      tableClassName="min-w-0 max-w-full border-0 mt-[-1.5rem] [&_table]:bg-[#f7f7f9] [&_table]:border-spacing-y-6 [&_table>tbody>tr]:bg-white [&_table>tbody>tr>td]:border"
    />
  );
}
